/**
 * Grouped event-type checkboxes (TRUS-685), shared by the channel "Connect
 * events" dialog and "Events I follow".
 *
 * Types that carry member-linked data (`phi`) get a "Member data" marker and a
 * note saying how they are handled. A caller can lock a type with a reason
 * (a public channel cannot take PHI types): a locked type cannot be checked,
 * shows the reason in a tooltip and to screen readers, and can still be
 * unchecked if it was saved before the lock applied.
 */

import React from 'react';
import { Lock } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { SystemEventType } from '@/services/systemEventsApi';
import { groupLabel, typeLabel, type EventTypeGroup } from './eventCatalog';

interface EventTypeChecklistProps {
  groups: EventTypeGroup[];
  /** Every type the server sent (for labels). */
  allTypes: SystemEventType[];
  selected: string[];
  onToggle: (type: string, on: boolean) => void;
  disabled?: boolean;
  /** Why this type cannot be turned on here, or null when it can. */
  lockReason?: (type: SystemEventType) => string | null;
  /** The note shown under a group that contains member-data types. */
  phiNote: string;
  /** Prefix for test ids (`event-type-<type>` by default). */
  testIdPrefix?: string;
}

export function EventTypeChecklist({
  groups,
  allTypes,
  selected,
  onToggle,
  disabled,
  lockReason,
  phiNote,
  testIdPrefix = 'event-type',
}: EventTypeChecklistProps) {
  const { t } = useAppTranslation();
  const phiLabel = t('messaging.systemEvents.phiBadge', 'Member data');

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        {groups.map((group) => {
          const hasPhi = group.types.some((x) => x.phi);
          return (
            <fieldset key={group.id} className="space-y-1.5" data-testid={`channel-events-group-${group.id}`}>
              <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {groupLabel(group.id, t)}
              </legend>
              {group.types.map((type) => {
                const checked = selected.includes(type.type);
                const reason = lockReason?.(type) ?? null;
                const label = typeLabel(type.type, allTypes, t);
                const reasonId = reason ? `${testIdPrefix}-${type.type}-locked` : undefined;
                const row = (
                  <label
                    className={`flex items-start gap-2 text-sm ${reason && !checked ? 'cursor-not-allowed opacity-60' : ''}`}
                    data-testid={`${testIdPrefix}-row-${type.type}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-[var(--primary,#2563EB)]"
                      checked={checked}
                      onChange={(e) => onToggle(type.type, e.target.checked)}
                      // A locked type can be unchecked, never checked.
                      disabled={disabled || (!!reason && !checked)}
                      aria-describedby={reasonId}
                      data-testid={`${testIdPrefix}-${type.type}`}
                    />
                    <span className="min-w-0">
                      <span className="font-medium">{label}</span>
                      {type.phi && (
                        <span
                          className="ml-2 inline-flex items-center gap-0.5 rounded border border-border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground"
                          data-testid={`${testIdPrefix}-phi-${type.type}`}
                        >
                          <Lock className="h-3 w-3" aria-hidden="true" />
                          {phiLabel}
                        </span>
                      )}
                      {type.description && (
                        <span className="block text-xs text-muted-foreground">{type.description}</span>
                      )}
                      {reason && (
                        <span id={reasonId} className="sr-only">
                          {reason}
                        </span>
                      )}
                    </span>
                  </label>
                );
                return reason ? (
                  <Tooltip key={type.type}>
                    <TooltipTrigger asChild>
                      <div>{row}</div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      {reason}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div key={type.type}>{row}</div>
                );
              })}
              {hasPhi && (
                <p className="flex items-start gap-1 text-xs text-muted-foreground" data-testid={`phi-note-${group.id}`}>
                  <Lock className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
                  {phiNote}
                </p>
              )}
            </fieldset>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

export default EventTypeChecklist;
